import React, { useCallback, useEffect, useState } from "react";
import { useHistory, useParams } from "react-router";
import { FormattedMessage, useIntl } from "react-intl";
import {
  Button,
  Card,
  Form,
  Col,
  Row,
  Alert,
  Container as ContainerBootstrap,
} from "react-bootstrap";
import { FormProvider, useForm, Controller, useFormContext } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { v4 } from "uuid";
import ContentWrapper from "../../components/content-wrapper";
import { SectionHeading } from "../../components/section-heading";
import { useProperty } from "../../components/property-provider";
import { withAuthorizationVerifier } from "../../components/authorization-verifier";
import { FullWidthLoader } from "../../components/full-width-loader";
import { upgradeManagerMessages } from "./upgradeManager.messages";
import {
  UpgradeTarget,
  GuaranteeAppliedEnum,
  PenaltyAppliedEnum,
} from "./domain/upgradeConfiguration.schema";
import { UpgradeConfigurationEntity } from "./domain/upgradeConfiguration.entity";
import {
  createUpgradeConfiguration,
  getUpgradeConfiguration,
  updateUpgradeConfiguration,
} from "./infrastructure/upgradeConfiguration.api";
import { useHttpClient } from "../../lib/http";
import { useConfig } from "../../lib/config";
import { TargetModal } from "./components/TargetModal";
import { TargetsTable } from "./components/TargetsTable";
import { InputToggle } from "../bundle/components/InputToggle";
import { DateRangesInput, DateRange } from "./components/DateRangesInput";
import { DeltaFields } from "./components/DeltaFields";
import { StyledCard, LinkStyled, ButtonContainer, WitLabel } from "./styles";
import {
  UpgradeConfigurationFormSchema,
  defaultUpgradeConfigurationValues,
  UpgradeConfigurationFormInput,
  UpgradeConfigurationFormType,
  isValidEmail,
  hasBothDates,
} from "./utils/validation";
import { useDefaultCurrency } from "./hooks/useDefaultCurrency.hook";

const messages = upgradeManagerMessages;

interface RouteParams {
  id?: string;
}

interface ApiErrorResponse {
  response?: {
    status: number;
    data?: {
      message?: string;
      metadata?: {
        name?: string;
        message?: string;
      };
    };
  };
  message?: string;
}

function UpgradeManagerForm() {
  const intl = useIntl();
  const history = useHistory();
  const { id } = useParams<RouteParams>();
  const config = useConfig();
  const httpClient = useHttpClient(config.sellingApiEndpoint);
  const propertyInfo = useProperty();
  const { defaultCurrency } = useDefaultCurrency();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isConflictError, setIsConflictError] = useState(false);
  const [showTargetModal, setShowTargetModal] = useState(false);
  const [editingTargetIndex, setEditingTargetIndex] = useState<number | null>(null);
  const [showAdvancedConfiguration, setShowAdvancedConfiguration] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  const methods = useForm<UpgradeConfigurationFormInput, unknown, UpgradeConfigurationFormType>({
    resolver: zodResolver(UpgradeConfigurationFormSchema),
    defaultValues: {
      ...defaultUpgradeConfigurationValues,
      id: v4(),
      propertyTicker: propertyInfo.ticker,
    },
    mode: "onBlur",
  });

  const { reset } = methods;
  const formData = methods.watch();

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setIsInitialized(false);

      if (!id || id === "new") {
        reset({
          ...defaultUpgradeConfigurationValues,
          id: v4(),
          propertyTicker: propertyInfo.ticker,
          createdAt: new Date(),
          updatedAt: new Date(),
        });
        setIsInitialized(true);
        return;
      }

      setLoading(true);
      try {
        const data = await getUpgradeConfiguration(httpClient, id);
        if (cancelled) return;

        reset(data);
        setIsInitialized(true);
      } catch (err) {
        console.error("Error loading upgrade configuration:", err);
        if (!cancelled) setError(intl.formatMessage(messages.errorLoading));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();

    return () => {
      cancelled = true;
    };
  }, [id, httpClient, reset, propertyInfo.ticker, intl]);

  const handleSubmit = useCallback(
    async (formValues: UpgradeConfigurationFormType) => {
      setError(null);
      setIsConflictError(false);
      setLoading(true);

      try {
        const invalidEmails = formValues.propertyRecipients?.filter(
          email => email && !isValidEmail(email)
        );

        if (invalidEmails && invalidEmails.length > 0) {
          setError(
            `${intl.formatMessage(messages.errorInvalidEmails)}: ${invalidEmails.join(", ")}`
          );
          setLoading(false);
          return;
        }

        if (hasBothDates(formValues.generalSellingActivationRule)) {
          setError(intl.formatMessage(messages.errorBothDates));
          setLoading(false);
          return;
        }

        if (!formValues.sellingTargets || formValues.sellingTargets.length === 0) {
          setError(intl.formatMessage(messages.errorNoTargets));
          setLoading(false);
          return;
        }

        const targetWithBothDates = (formValues.sellingTargets || []).findIndex(t =>
          hasBothDates(t.sellingActivationRule ?? {})
        );
        if (targetWithBothDates !== -1) {
          setError(
            `${intl.formatMessage(messages.errorBothDates)} (target ${targetWithBothDates + 1})`
          );
          setLoading(false);
          return;
        }

        const cleanActivationRule = Object.fromEntries(
          Object.entries(formValues.generalSellingActivationRule || {}).filter(
            ([_key, v]) => v !== undefined && v !== null
          )
        );

        const entity = UpgradeConfigurationEntity.of({
          id: formValues.id!,
          active: formValues.active || false,
          propertyTicker: formValues.propertyTicker,
          propertyRecipients: formValues.propertyRecipients || [],
          sellingLocations: formValues.sellingLocations || [],
          notificationChannels: formValues.notificationChannels || [],
          sellingConfigurationType: formValues.sellingConfigurationType,
          sellingTargets: formValues.sellingTargets || [],
          generalSellingActivationRule: cleanActivationRule,
          createdAt: formValues.createdAt ?? new Date(),
          updatedAt: new Date(),
        } as Parameters<typeof UpgradeConfigurationEntity.of>[0]);

        if (id && id !== "new") {
          await updateUpgradeConfiguration(httpClient, id, entity);
        } else {
          await createUpgradeConfiguration(httpClient, entity);
        }
        alert(intl.formatMessage(messages.updateSuccessDescription));
        history.push(`/${propertyInfo.ticker}/upgrade-configuration`);
      } catch (err) {
        console.error("Error saving upgrade configuration:", err);

        let errorMessage = intl.formatMessage(messages.errorSaving);
        let isConflict = false;

        const apiErr = err as ApiErrorResponse;

        if (apiErr?.response?.data) {
          const errorData = apiErr.response.data;

          if (errorData.metadata?.name === "ConflictException" || apiErr.response.status === 409) {
            isConflict = true;
            errorMessage = intl.formatMessage(messages.errorConflict);
          } else if (apiErr.response.status === 400) {
            errorMessage = intl.formatMessage(messages.errorValidation);
            const detail = errorData.metadata?.message || errorData.message;
            if (detail) errorMessage += `: ${detail}`;
          } else {
            const detail = errorData.metadata?.message || errorData.message;
            if (detail) errorMessage += `: ${detail}`;
          }
        } else if (apiErr?.message) {
          errorMessage += `: ${apiErr.message}`;
        }

        setIsConflictError(isConflict);
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    },
    [id, httpClient, history, propertyInfo.ticker, intl]
  );

  const handleBack = useCallback(() => {
    history.push(`/${propertyInfo.ticker}/upgrade-configuration`);
  }, [history, propertyInfo.ticker]);

  const handleAddTarget = useCallback(() => {
    setEditingTargetIndex(null);
    setShowTargetModal(true);
  }, []);

  const handleEditTarget = useCallback((index: number) => {
    setEditingTargetIndex(index);
    setShowTargetModal(true);
  }, []);

  const handleDeleteTarget = useCallback(
    (index: number) => {
      const currentTargets = methods.getValues("sellingTargets") || [];
      methods.setValue(
        "sellingTargets",
        currentTargets.filter((_, i) => i !== index)
      );
    },
    [methods]
  );

  const handleSaveTarget = useCallback(
    (target: UpgradeTarget) => {
      const currentTargets = methods.getValues("sellingTargets") || [];
      if (editingTargetIndex !== null) {
        methods.setValue(
          "sellingTargets",
          currentTargets.map((t, i) => (i === editingTargetIndex ? target : t))
        );
      } else {
        methods.setValue("sellingTargets", [...currentTargets, target]);
      }
      setShowTargetModal(false);
      setEditingTargetIndex(null);
    },
    [editingTargetIndex, methods]
  );

  if (loading || !isInitialized) {
    return <FullWidthLoader />;
  }

  return (
    <ContentWrapper>
      <FormProvider {...methods}>
        <SectionHeading>
          <FormattedMessage {...messages.entityName} />
        </SectionHeading>

        <ContainerBootstrap fluid>
          <Form onSubmit={methods.handleSubmit(handleSubmit)}>
            <StyledCard>
              <Card.Body>
                <Form.Group as={Row} className="mb-3">
                  <Form.Label column sm={12}>
                    <WitLabel>
                      <FormattedMessage {...messages.active} />
                    </WitLabel>
                  </Form.Label>
                  <Col sm={2}>
                    <Controller
                      name="active"
                      control={methods.control}
                      render={({ field: { ref: _ref, value, ...field } }) => (
                        <InputToggle className="witadmin-bs3" value={value ?? false} {...field} />
                      )}
                    />
                  </Col>
                </Form.Group>

                <Form.Group as={Row} className="mb-3">
                  <Form.Label column sm={12}>
                    <FormattedMessage {...messages.identifier} />
                  </Form.Label>
                  <Col sm={3}>
                    <Form.Control type="text" value={formData.id || ""} readOnly disabled />
                  </Col>
                </Form.Group>

                <Form.Group as={Row} className="mb-2">
                  <Form.Label column sm={12}>
                    <WitLabel>
                      <FormattedMessage {...messages.generalConfiguration} />
                    </WitLabel>
                  </Form.Label>
                </Form.Group>

                <Form.Group as={Row} className="mb-2">
                  <Form.Label column sm={2}>
                    <FormattedMessage {...messages.sellingLocations} />
                  </Form.Label>
                  <Col sm={3}>
                    <Form.Control type="text" value="NOTIFICATION" readOnly disabled />
                  </Col>
                </Form.Group>

                <Form.Group as={Row} className="mb-2">
                  <Form.Label column sm={2}>
                    <FormattedMessage {...messages.notificationChannels} />
                  </Form.Label>
                  <Col sm={3}>
                    <Form.Control type="text" value="EMAIL" readOnly disabled />
                  </Col>
                </Form.Group>

                <Form.Group as={Row} className="mb-2">
                  <Form.Label column sm={2}>
                    <FormattedMessage {...messages.propertyRecipients} />
                  </Form.Label>
                  <Col sm={3}>
                    <Controller
                      name="propertyRecipients"
                      control={methods.control}
                      render={({ field: { value, onChange } }) => (
                        <Form.Control
                          type="text"
                          placeholder={intl.formatMessage(messages.emailPlaceholder)}
                          value={value?.join(", ") || ""}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            onChange(
                              e.target.value
                                .split(",")
                                .map(s => s.trim())
                                .filter(s => s.length > 0)
                            );
                          }}
                        />
                      )}
                    />
                    <Form.Text className="text-muted">
                      <FormattedMessage {...messages.propertyRecipientsHelp} />
                    </Form.Text>
                  </Col>
                </Form.Group>

                <Form.Group as={Row} className="mb-2">
                  <Form.Label column sm={12}>
                    <WitLabel>
                      <FormattedMessage {...messages.generalSellingActivationRules} />
                    </WitLabel>
                  </Form.Label>
                </Form.Group>

                <Form.Group as={Row} className="mb-2">
                  <Form.Label column sm={2}>
                    <FormattedMessage {...messages.minReleaseInDays} />
                  </Form.Label>
                  <Col sm={3}>
                    <Form.Control
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      value={formData.generalSellingActivationRule?.minReleaseInDays ?? ""}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        const raw = e.target.value.replace(/[^0-9]/g, "");
                        methods.setValue(
                          "generalSellingActivationRule.minReleaseInDays",
                          raw === "" ? undefined : parseInt(raw, 10)
                        );
                      }}
                    />
                  </Col>
                </Form.Group>

                <Form.Group as={Row} className="mb-2">
                  <Form.Label column sm={2}>
                    <FormattedMessage {...messages.maxReleaseInDays} /> *
                  </Form.Label>
                  <Col sm={3}>
                    <Form.Control
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      required
                      value={formData.generalSellingActivationRule?.maxReleaseInDays ?? ""}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        const raw = e.target.value.replace(/[^0-9]/g, "");
                        methods.setValue(
                          "generalSellingActivationRule.maxReleaseInDays",
                          raw === "" ? 0 : parseInt(raw, 10)
                        );
                      }}
                    />
                  </Col>
                </Form.Group>

                <DeltaFields
                  label={<FormattedMessage {...messages.minDelta} />}
                  delta={formData.generalSellingActivationRule?.minAmountDelta}
                  onChange={delta =>
                    methods.setValue("generalSellingActivationRule.minAmountDelta", delta)
                  }
                  defaultCurrency={defaultCurrency}
                />

                <DeltaFields
                  label={<FormattedMessage {...messages.maxDelta} />}
                  delta={formData.generalSellingActivationRule?.maxAmountDelta}
                  onChange={delta =>
                    methods.setValue("generalSellingActivationRule.maxAmountDelta", delta)
                  }
                  defaultCurrency={defaultCurrency}
                />

                {!showAdvancedConfiguration && (
                  <div className="mb-2">
                    <LinkStyled
                      onClick={() => setShowAdvancedConfiguration(!showAdvancedConfiguration)}>
                      <FormattedMessage {...messages.seeAdvancedOptions} />
                    </LinkStyled>
                  </div>
                )}

                {showAdvancedConfiguration && (
                  <AdvancedConfigurationFields
                    onToggle={() => setShowAdvancedConfiguration(!showAdvancedConfiguration)}
                  />
                )}

                <Form.Group as={Row} className="mb-2">
                  <Form.Label column sm={12}>
                    <WitLabel>
                      <FormattedMessage {...messages.sellingTargets} />
                    </WitLabel>
                  </Form.Label>
                </Form.Group>

                <Button variant="secondary" onClick={handleAddTarget} className="mt-2">
                  <FormattedMessage {...messages.newTarget} />
                </Button>

                <TargetsTable
                  targets={(formData.sellingTargets || []) as UpgradeTarget[]}
                  onEdit={handleEditTarget}
                  onDelete={handleDeleteTarget}
                  onDuplicate={index => {
                    const target = formData.sellingTargets?.[index];
                    if (target) {
                      const currentTargets = methods.getValues("sellingTargets") || [];
                      methods.setValue("sellingTargets", [...currentTargets, { ...target }]);
                    }
                  }}
                />
                <ButtonContainer>
                  <Button variant="primary" type="submit" disabled={loading}>
                    {loading ? (
                      <FormattedMessage {...messages.loading} />
                    ) : (
                      <FormattedMessage {...messages.save} />
                    )}
                  </Button>
                  <Button variant="secondary" onClick={handleBack}>
                    <FormattedMessage {...messages.back} />
                  </Button>
                </ButtonContainer>

                {error && (
                  <Alert variant="danger" className="mt-3">
                    <p>{error}</p>
                    {isConflictError && (
                      <>
                        <hr />
                        <p className="mb-0">
                          <strong>
                            <FormattedMessage {...messages.note} />
                          </strong>{" "}
                          <FormattedMessage {...messages.errorConflictHelp} />
                        </p>
                      </>
                    )}
                  </Alert>
                )}
              </Card.Body>
            </StyledCard>
          </Form>
        </ContainerBootstrap>

        {showTargetModal && (
          <TargetModal
            show={showTargetModal}
            target={
              editingTargetIndex !== null
                ? (formData.sellingTargets?.[editingTargetIndex] as UpgradeTarget | undefined)
                : undefined
            }
            onHide={() => {
              setShowTargetModal(false);
              setEditingTargetIndex(null);
            }}
            onSave={handleSaveTarget}
          />
        )}
      </FormProvider>
    </ContentWrapper>
  );
}

function AdvancedConfigurationFields({ onToggle }: { onToggle: () => void }): JSX.Element {
  const intl = useIntl();
  const { watch, setValue } = useFormContext<
    UpgradeConfigurationFormInput,
    unknown,
    UpgradeConfigurationFormType
  >();
  const formData = watch();

  return (
    <>
      <Form.Group as={Row} className="mb-2">
        <Form.Label column sm={2}>
          <FormattedMessage {...messages.minStayInDays} />
        </Form.Label>
        <Col sm={3}>
          <Form.Control
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            placeholder="1"
            value={formData.generalSellingActivationRule?.minStayInDays ?? ""}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const raw = e.target.value.replace(/[^0-9]/g, "");
              setValue(
                "generalSellingActivationRule.minStayInDays",
                raw === "" ? undefined : parseInt(raw, 10)
              );
            }}
          />
        </Col>
      </Form.Group>

      <Form.Group as={Row} className="mb-2">
        <Form.Label column sm={2}>
          <FormattedMessage {...messages.maxStayInDays} />
        </Form.Label>
        <Col sm={3}>
          <Form.Control
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            placeholder="100"
            value={formData.generalSellingActivationRule?.maxStayInDays ?? ""}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const raw = e.target.value.replace(/[^0-9]/g, "");
              setValue(
                "generalSellingActivationRule.maxStayInDays",
                raw === "" ? undefined : parseInt(raw, 10)
              );
            }}
          />
        </Col>
      </Form.Group>

      <Form.Group as={Row} className="mb-2">
        <Form.Label column sm={2}>
          <FormattedMessage {...messages.minSourceAvailability} />
        </Form.Label>
        <Col sm={3}>
          <Form.Control
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            placeholder="1"
            value={formData.generalSellingActivationRule?.minSourceAvailability ?? ""}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const raw = e.target.value.replace(/[^0-9]/g, "");
              setValue(
                "generalSellingActivationRule.minSourceAvailability",
                raw === "" ? undefined : parseInt(raw, 10)
              );
            }}
          />
        </Col>
      </Form.Group>

      <Form.Group as={Row} className="mb-2">
        <Form.Label column sm={2}>
          <FormattedMessage {...messages.maxSourceAvailability} />
        </Form.Label>
        <Col sm={3}>
          <Form.Control
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            placeholder="500"
            value={formData.generalSellingActivationRule?.maxSourceAvailability ?? ""}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const raw = e.target.value.replace(/[^0-9]/g, "");
              setValue(
                "generalSellingActivationRule.maxSourceAvailability",
                raw === "" ? undefined : parseInt(raw, 10)
              );
            }}
          />
        </Col>
      </Form.Group>

      <Form.Group as={Row} className="mb-2">
        <Form.Label column sm={2}>
          <FormattedMessage {...messages.minUpgradeToAvailability} />
        </Form.Label>
        <Col sm={3}>
          <Form.Control
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            placeholder="1"
            value={formData.generalSellingActivationRule?.minUpgradeToAvailability ?? ""}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const raw = e.target.value.replace(/[^0-9]/g, "");
              setValue(
                "generalSellingActivationRule.minUpgradeToAvailability",
                raw === "" ? undefined : parseInt(raw, 10)
              );
            }}
          />
        </Col>
      </Form.Group>

      <Form.Group as={Row} className="mb-2">
        <Form.Label column sm={2}>
          <FormattedMessage {...messages.maxUpgradeToAvailability} />
        </Form.Label>
        <Col sm={3}>
          <Form.Control
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            placeholder="500"
            value={formData.generalSellingActivationRule?.maxUpgradeToAvailability ?? ""}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const raw = e.target.value.replace(/[^0-9]/g, "");
              setValue(
                "generalSellingActivationRule.maxUpgradeToAvailability",
                raw === "" ? undefined : parseInt(raw, 10)
              );
            }}
          />
        </Col>
      </Form.Group>

      <Form.Group as={Row} className="mb-2">
        <Form.Label column sm={2}>
          <FormattedMessage {...messages.guarantee} />
        </Form.Label>
        <Col sm={3}>
          <Form.Control
            as="select"
            value={
              formData.generalSellingActivationRule?.guaranteeApplied || GuaranteeAppliedEnum.IGNORE
            }
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setValue(
                "generalSellingActivationRule.guaranteeApplied",
                e.target.value as GuaranteeAppliedEnum
              );
            }}>
            <option value={GuaranteeAppliedEnum.IGNORE}>
              {intl.formatMessage(messages.guaranteeIgnore)}
            </option>
            <option value={GuaranteeAppliedEnum.HAS_GUARANTEE_APPLIED}>
              {intl.formatMessage(messages.guaranteeHas)}
            </option>
            <option value={GuaranteeAppliedEnum.NO_GUARANTEE_APPLIED}>
              {intl.formatMessage(messages.guaranteeNo)}
            </option>
          </Form.Control>
        </Col>
      </Form.Group>

      <Form.Group as={Row} className="mb-2">
        <Form.Label column sm={2}>
          <FormattedMessage {...messages.penalty} />
        </Form.Label>
        <Col sm={3}>
          <Form.Control
            as="select"
            value={
              formData.generalSellingActivationRule?.penaltyApplied || PenaltyAppliedEnum.IGNORE
            }
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setValue(
                "generalSellingActivationRule.penaltyApplied",
                e.target.value as PenaltyAppliedEnum
              );
            }}>
            <option value={PenaltyAppliedEnum.IGNORE}>
              {intl.formatMessage(messages.penaltyIgnore)}
            </option>
            <option value={PenaltyAppliedEnum.HAS_PENALTY_APPLIED}>
              {intl.formatMessage(messages.penaltyHas)}
            </option>
            <option value={PenaltyAppliedEnum.NO_PENALTY_APPLIED}>
              {intl.formatMessage(messages.penaltyNo)}
            </option>
          </Form.Control>
        </Col>
      </Form.Group>

      <Form.Group as={Row} className="mb-2">
        <Form.Label column sm={2}>
          <FormattedMessage {...messages.includedDates} />
        </Form.Label>
        <Col sm={3}>
          <DateRangesInput
            value={(formData.generalSellingActivationRule?.includedDates || []) as DateRange[]}
            onChange={ranges =>
              setValue(
                "generalSellingActivationRule.includedDates",
                ranges as { start: Date; end: Date }[]
              )
            }
          />
        </Col>
      </Form.Group>

      <Form.Group as={Row} className="mb-2">
        <Form.Label column sm={2}>
          <FormattedMessage {...messages.excludedDates} />
        </Form.Label>
        <Col sm={3}>
          <DateRangesInput
            value={(formData.generalSellingActivationRule?.excludedDates || []) as DateRange[]}
            onChange={ranges =>
              setValue(
                "generalSellingActivationRule.excludedDates",
                ranges as { start: Date; end: Date }[]
              )
            }
          />
        </Col>
      </Form.Group>

      <Form.Group as={Row} className="mb-2">
        <Form.Label column sm={2}>
          <FormattedMessage {...messages.promoCodes} />
        </Form.Label>
        <Col sm={3}>
          <Form.Control
            type="text"
            value={formData.generalSellingActivationRule?.promoCodes?.join(", ") || ""}
            onChange={e => {
              const value = e.target.value
                .split(",")
                .map(s => s.trim())
                .filter(Boolean);
              setValue("generalSellingActivationRule.promoCodes", value);
            }}
          />
        </Col>
      </Form.Group>

      <Form.Group as={Row} className="mb-2">
        <Form.Label column sm={2}>
          <FormattedMessage {...messages.includedTags} />
        </Form.Label>
        <Col sm={3}>
          <Form.Control
            type="text"
            value={formData.generalSellingActivationRule?.includedTags?.join(", ") || ""}
            onChange={e => {
              const value = e.target.value
                .split(",")
                .map(s => s.trim())
                .filter(Boolean);
              setValue("generalSellingActivationRule.includedTags", value);
            }}
          />
        </Col>
      </Form.Group>

      <div className="mb-2">
        <LinkStyled onClick={onToggle}>
          <FormattedMessage {...messages.hideAdvancedOptions} />
        </LinkStyled>
      </div>
    </>
  );
}

export default withAuthorizationVerifier(UpgradeManagerForm);
